import ajax from '../../utils/ajax';

function defaultFilters() {
    return {
        q: '',
        status: null,
        catalog_id: null,
        location_type: null,
        warehouse_id: null,
        building_process_id: null,
        object_group_id: null,
        object_group_scope_id: null,
        responsible_user_id: null,
        archive: false
    };
}

function defaultFilterOptions() {
    return {
        status: [],
        catalog_id: [],
        warehouse_id: [],
        building_process_id: [],
        object_group_id: [],
        responsible_user_id: []
    };
}

function defaultPositionFieldOptions() {
    return {
        accounting_company_names: []
    };
}

const HIDDEN_FILE_ADDED_HISTORY_ACTIONS = ['files_added', 'note_files_added'];

function visibleHistoryRows(rows) {
    return (Array.isArray(rows) ? rows : [])
        .filter(row => !HIDDEN_FILE_ADDED_HISTORY_ACTIONS.includes(row && row.action));
}

async function fetchUnitDetails(id) {
    const unit = await ajax.getMechanicAssetUnit(id);
    const [history, notes] = await Promise.all([
        unit && unit.can_read_history === true
            ? ajax.getMechanicAssetHistory(id)
            : Promise.resolve([]),
        ajax.getMechanicAssetNotes(id)
    ]);
    return {unit, history, notes};
}

export default {
    namespaced: true,
    state: {
        units: [],
        total: 0,
        limit: 50,
        offset: 0,
        filters: defaultFilters(),
        sortBy: 'id',
        sortDesc: true,
        filterOptions: defaultFilterOptions(),
        statuses: [],
        locationTypes: [],
        objectGroups: [],
        positionFieldOptions: defaultPositionFieldOptions(),
        fuelNormTypes: [],
        warehouses: [],
        catalog: [],
        accessSettings: {},
        selectedUnit: null,
        selectedHistory: [],
        selectedNotes: [],
        loading: false,
        cardLoading: false
    },
    mutations: {
        setUnits(state, payload) {
            const source = payload || {};
            state.units = Array.isArray(source.rows) ? source.rows : [];
            state.total = Number(source.count) || 0;
            state.limit = Number(source.limit) || state.limit;
            state.offset = Number(source.offset) || 0;
            if (Object.prototype.hasOwnProperty.call(source, 'filter_options')) {
                state.filterOptions = Object.assign(defaultFilterOptions(), source.filter_options || {});
            }
        },
        setFilters(state, filters) {
            state.filters = Object.assign({}, state.filters, filters || {});
        },
        resetFilters(state) {
            state.filters = defaultFilters();
            state.offset = 0;
        },
        setPagination(state, {limit, offset}) {
            state.limit = Number(limit) || state.limit;
            state.offset = Number(offset) || 0;
        },
        setSort(state, {sortBy, sortDesc}) {
            state.sortBy = sortBy || 'id';
            state.sortDesc = Boolean(sortDesc);
            state.offset = 0;
        },
        setStatuses(state, value) {
            state.statuses = Array.isArray(value) ? value : [];
        },
        setLocationTypes(state, value) {
            state.locationTypes = Array.isArray(value) ? value : [];
        },
        setObjectGroups(state, value) {
            state.objectGroups = Array.isArray(value) ? value : [];
        },
        setPositionFieldOptions(state, value) {
            state.positionFieldOptions = Object.assign(defaultPositionFieldOptions(), value || {});
        },
        setFuelNormTypes(state, value) {
            state.fuelNormTypes = Array.isArray(value) ? value : [];
        },
        setWarehouses(state, value) {
            state.warehouses = Array.isArray(value) ? value : [];
        },
        setCatalog(state, value) {
            state.catalog = Array.isArray(value) ? value : [];
        },
        setAccessSettings(state, value) {
            state.accessSettings = value || {};
        },
        setSelectedUnit(state, value) {
            state.selectedUnit = value;
        },
        setSelectedHistory(state, value) {
            state.selectedHistory = visibleHistoryRows(value);
        },
        setSelectedNotes(state, value) {
            state.selectedNotes = Array.isArray(value) ? value : [];
        },
        setLoading(state, value) {
            state.loading = Boolean(value);
        },
        setCardLoading(state, value) {
            state.cardLoading = Boolean(value);
        }
    },
    actions: {
        async fetchDictionaries({commit}) {
            const [statuses, locationTypes, positionFieldOptions, fuelNormTypes, warehouses, catalog, objectGroups] = await Promise.all([
                ajax.getMechanicAssetStatuses(),
                ajax.getMechanicAssetLocationTypes('registry'),
                ajax.getMechanicAssetPositionFieldOptions(),
                ajax.getMechanicAssetFuelNormTypes(),
                ajax.getMechanicAssetWarehouses({active: true}),
                ajax.getMechanicAssetCatalog({active: true, limit: 200}),
                ajax.getMechanicAssetObjectGroups()
            ]);
            commit('setStatuses', statuses);
            commit('setLocationTypes', locationTypes);
            commit('setObjectGroups', objectGroups);
            commit('setPositionFieldOptions', positionFieldOptions);
            commit('setFuelNormTypes', fuelNormTypes);
            commit('setWarehouses', warehouses);
            commit('setCatalog', catalog && Array.isArray(catalog.rows) ? catalog.rows : catalog);
        },
        async refreshDictionaries({dispatch}) {
            return dispatch('fetchDictionaries');
        },
        async fetchAccessSettings({commit}) {
            const data = await ajax.getMechanicAssetAccessSettings();
            commit('setAccessSettings', data);
            return data;
        },
        async fetchPositionFieldOptions({commit}) {
            const data = await ajax.getMechanicAssetPositionFieldOptions();
            commit('setPositionFieldOptions', data);
            return data;
        },
        async updateAccessSettings({commit}, accessConfig) {
            const data = await ajax.updateMechanicAssetAccessSettings(accessConfig);
            commit('setAccessSettings', data);
            return data;
        },
        async fetchUnits({commit, state}, options = {}) {
            const silent = options.silent === true;
            if (!silent) {
                commit('setLoading', true);
            }
            try {
                const params = Object.assign({}, state.filters, {
                    limit: state.limit,
                    offset: state.offset,
                    sort_by: state.sortBy,
                    sort_desc: state.sortDesc
                });
                if (options.filterOptions === false) {
                    params.filter_options = false;
                }
                const data = await ajax.getMechanicAssetUnits(params);
                commit('setUnits', data || {});
                return data;
            } finally {
                if (!silent) {
                    commit('setLoading', false);
                }
            }
        },
        async applyFilters({commit, dispatch}, filters) {
            commit('setFilters', filters);
            commit('setPagination', {offset: 0});
            return dispatch('fetchUnits');
        },
        async setPage({commit, dispatch, state}, page) {
            const currentPage = Number(page) || 1;
            commit('setPagination', {
                limit: state.limit,
                offset: (currentPage - 1) * state.limit
            });
            return dispatch('fetchUnits', {filterOptions: false});
        },
        async setLimit({commit, dispatch, state}, limit) {
            const nextLimit = Number(limit) || state.limit;
            commit('setPagination', {
                limit: nextLimit,
                offset: 0
            });
            return dispatch('fetchUnits', {filterOptions: false});
        },
        async setSort({commit, dispatch}, payload) {
            commit('setSort', payload || {});
            return dispatch('fetchUnits', {filterOptions: false});
        },
        async openUnit({commit}, id) {
            commit('setCardLoading', true);
            commit('setSelectedUnit', null);
            commit('setSelectedHistory', []);
            commit('setSelectedNotes', []);
            try {
                const {unit, history, notes} = await fetchUnitDetails(id);
                commit('setSelectedUnit', unit);
                commit('setSelectedHistory', history);
                commit('setSelectedNotes', notes);
                return unit;
            } finally {
                commit('setCardLoading', false);
            }
        },
        async refreshSelectedUnit({commit, state}) {
            if (!state.selectedUnit || !state.selectedUnit.id) {
                return null;
            }
            const {unit, history, notes} = await fetchUnitDetails(state.selectedUnit.id);
            commit('setSelectedUnit', unit);
            commit('setSelectedHistory', history);
            commit('setSelectedNotes', notes);
            return unit;
        },
        async createUnit({dispatch}, payload) {
            const unit = await ajax.createMechanicAssetUnit(payload);
            await Promise.all([
                dispatch('fetchUnits'),
                dispatch('fetchPositionFieldOptions')
            ]);
            return unit;
        },
        async updateUnit({dispatch}, {id, payload}) {
            const unit = await ajax.updateMechanicAssetUnit(id, payload);
            await Promise.all([
                dispatch('openUnit', id),
                dispatch('fetchUnits'),
                dispatch('fetchPositionFieldOptions')
            ]);
            return unit;
        },
        async archiveUnit({dispatch}, {id, payload}) {
            const unit = await ajax.archiveMechanicAssetUnit(id, payload || {});
            await dispatch('openUnit', id);
            await dispatch('fetchUnits');
            return unit;
        },
        async addNote({dispatch}, {id, payload, refresh}) {
            const note = await ajax.createMechanicAssetNote(id, payload);
            if (refresh !== false) {
                await dispatch('openUnit', id);
                await dispatch('fetchUnits');
            }
            return note;
        },
        async createFuelNorm({dispatch}, {id, payload}) {
            const norm = await ajax.createMechanicAssetFuelNorm(id, payload);
            await dispatch('openUnit', id);
            await dispatch('fetchUnits');
            return norm;
        },
        async updateFuelNorm({dispatch, state}, {id, payload}) {
            const norm = await ajax.updateMechanicAssetFuelNorm(id, payload);
            if (state.selectedUnit && state.selectedUnit.id) {
                await dispatch('openUnit', state.selectedUnit.id);
            }
            await dispatch('fetchUnits');
            return norm;
        },
        async deleteFuelNorm({dispatch, state}, {id, payload}) {
            const result = await ajax.deleteMechanicAssetFuelNorm(id, payload || {});
            if (state.selectedUnit && state.selectedUnit.id) {
                await dispatch('openUnit', state.selectedUnit.id);
            }
            await dispatch('fetchUnits');
            return result;
        },
        async recordMeter({dispatch}, {id, payload}) {
            const row = await ajax.createMechanicAssetMeterRecord(id, payload);
            await dispatch('openUnit', id);
            await dispatch('fetchUnits');
            return row;
        },
        async rentOut({dispatch}, {id, payload}) {
            const rental = await ajax.createMechanicAssetRental(id, payload);
            await dispatch('openUnit', id);
            await dispatch('fetchUnits');
            return rental;
        },
        async returnRental({dispatch, state}, {id, payload}) {
            const rental = await ajax.returnMechanicAssetRental(id, payload);
            if (state.selectedUnit && state.selectedUnit.id) {
                await dispatch('openUnit', state.selectedUnit.id);
            }
            await dispatch('fetchUnits');
            return rental;
        },
        closeUnit({commit}) {
            commit('setSelectedUnit', null);
            commit('setSelectedHistory', []);
            commit('setSelectedNotes', []);
        },
        async createWarehouse({dispatch}, payload) {
            const row = await ajax.createMechanicAssetWarehouse(payload);
            await dispatch('fetchDictionaries');
            return row;
        },
        async updateWarehouse({dispatch}, {id, payload}) {
            const row = await ajax.updateMechanicAssetWarehouse(id, payload);
            await dispatch('fetchDictionaries');
            await dispatch('fetchUnits');
            return row;
        },
        async archiveWarehouse({dispatch}, {id, payload}) {
            const row = await ajax.archiveMechanicAssetWarehouse(id, payload || {});
            await dispatch('fetchDictionaries');
            await dispatch('fetchUnits');
            return row;
        },
        async createCatalog({dispatch}, payload) {
            const row = await ajax.createMechanicAssetCatalog(payload);
            await dispatch('fetchDictionaries');
            return row;
        },
        async updateCatalog({dispatch}, {id, payload}) {
            const row = await ajax.updateMechanicAssetCatalog(id, payload);
            await dispatch('fetchDictionaries');
            await dispatch('fetchUnits');
            return row;
        },
        async archiveCatalog({dispatch}, {id, payload}) {
            const row = await ajax.archiveMechanicAssetCatalog(id, payload || {});
            await dispatch('fetchDictionaries');
            await dispatch('fetchUnits');
            return row;
        },
        async uploadFiles({dispatch}, {id, payload, refresh, config}) {
            const rows = await ajax.uploadMechanicAssetFiles(id, payload, config);
            if (refresh !== false) {
                await dispatch('openUnit', id);
            }
            return rows;
        },
        async uploadNoteFiles({dispatch}, {id, noteId, payload, refresh, config}) {
            const rows = await ajax.uploadMechanicAssetNoteFiles(id, noteId, payload, config);
            if (refresh !== false) {
                await dispatch('openUnit', id);
            }
            return rows;
        },
        async setPrimaryPhoto({dispatch}, {id, fileId, payload}) {
            await ajax.setMechanicAssetPrimaryPhoto(id, fileId, payload || {});
            const unit = await dispatch('openUnit', id);
            await dispatch('fetchUnits');
            return unit;
        },
        async deleteFile({dispatch}, {id, fileId}) {
            await ajax.deleteMechanicAssetFile(id, fileId);
            const unit = await dispatch('openUnit', id);
            await dispatch('fetchUnits');
            return unit;
        }
    }
};
